import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthenticationService } from '../services/authentication.service';
import { from, switchMap } from 'rxjs';
import { isInterceptedUrl } from './intercepted-urls';
import { APP_CONFIG } from '../../config/interfaces/iapp-settings.interface';

export const authInterceptor: HttpInterceptorFn = (
	req,
	next,
	authenticationService = inject(AuthenticationService),
	appSettings = inject(APP_CONFIG, { optional: true })
) => {
	let request = req;

	const idToken = authenticationService.getIDToken();

	// Only the learner-facing pre-auth onboarding steps are anonymous — they are
	// protected by a signed JWT in the request body, not a user session token.
	// Everything else under authorization/api/Onboarding (e.g. admin/regenerate)
	// and other endpoints that merely contain "onboarding" in their URL (e.g.
	// user/api/users/{id}/onboarding[/reset]) are authenticated. Match this exact
	// allowlist so we don't strip auth from those.
	const anonymousOnboardingEndpoints = [
		'/authorization/api/onboarding/initiate',
		'/authorization/api/onboarding/verify',
		'/authorization/api/onboarding/complete'
	];
	const requestPath = req.url.toLowerCase();
	const isOnboardingRequest = anonymousOnboardingEndpoints.some((endpoint) => requestPath.includes(endpoint));

	if (!idToken && !isOnboardingRequest && isInterceptedUrl(req.url, appSettings)) {
		throw new Error('ID Token is required for this request');
	}

	if (idToken && !isOnboardingRequest && isInterceptedUrl(req.url, appSettings)) {
		if (authenticationService.isTokenExpired(idToken)) {
			return from(authenticationService.refreshToken()).pipe(
				switchMap((token) => {
					request = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
					return next(request);
				})
			);
		}

		request = req.clone({ setHeaders: { Authorization: `Bearer ${idToken}` } });
	}

	return next(request);
};
